import { useMemo, useState } from "react";
import {
  ChevronIcon,
  CloseIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  UploadIcon,
} from "./Icons";
import type { PlaybookListItem, ReviewDocument } from "./types";
import "./review.css";

type PlaybookFilter = "All" | "Created by me" | "Shared" | "Firm-wide";

export interface PlaybooksListProps {
  playbooks: PlaybookListItem[];
  selectedPlaybookId?: string;
  reviewDocument?: ReviewDocument | null;
  currentUser?: string;
  onSelectPlaybook?: (playbook: PlaybookListItem) => void;
  onCreate?: () => void;
  onDelete?: (playbook: PlaybookListItem) => void | Promise<void>;
  onBrowseReviewDocument?: () => void;
  onChooseReviewDocumentFromRepository?: () => void;
  onReview?: (
    playbook: PlaybookListItem,
    document: ReviewDocument
  ) => void | Promise<void>;
}

export function PlaybooksList({
  playbooks,
  selectedPlaybookId,
  reviewDocument,
  currentUser,
  onSelectPlaybook,
  onCreate,
  onDelete,
  onBrowseReviewDocument,
  onChooseReviewDocumentFromRepository,
  onReview,
}: PlaybooksListProps) {
  const [filter, setFilter] = useState<PlaybookFilter>("All");
  const [query, setQuery] = useState("");
  const [creator, setCreator] = useState("All creators");
  const [localSelectedId, setLocalSelectedId] = useState(
    selectedPlaybookId || playbooks[0]?.id || ""
  );
  const [deleteTarget, setDeleteTarget] = useState<PlaybookListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const creators = useMemo(
    () => ["All creators", ...Array.from(new Set(playbooks.map((playbook) => playbook.createdBy)))],
    [playbooks]
  );
  const effectiveSelectedId = selectedPlaybookId ?? localSelectedId;
  const selectedPlaybook = playbooks.find((playbook) => playbook.id === effectiveSelectedId)
    || playbooks[0];

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return playbooks.filter((playbook) => {
      if (creator !== "All creators" && playbook.createdBy !== creator) return false;
      if (filter === "Created by me" && currentUser && playbook.createdBy !== currentUser) return false;
      if (filter === "Shared" && playbook.sharedWith === "Private") return false;
      if (filter === "Firm-wide" && playbook.sharedWith !== "Firm-wide") return false;
      if (!normalized) return true;
      return `${playbook.title} ${playbook.practiceArea || ""} ${playbook.description || ""}`
        .toLocaleLowerCase()
        .includes(normalized);
    });
  }, [creator, currentUser, filter, playbooks, query]);

  function select(playbook: PlaybookListItem) {
    setLocalSelectedId(playbook.id);
    onSelectPlaybook?.(playbook);
  }

  return (
    <section className="di-review di-playbooks" aria-label="Playbooks">
      <div className="di-playbooks__main">
        <div className="di-table-toolbar">
          <span className="di-tag">{playbooks.length} playbooks</span>
          <span className="di-spacer" />
          <button
            className="di-btn di-btn--primary"
            disabled={!onCreate}
            onClick={onCreate}
            title={!onCreate ? "Playbook creation is not connected." : undefined}
            type="button"
          >
            <PlusIcon />
            Create
          </button>
        </div>
        <div className="di-playbooks__content">
          <div className="di-playbook-filters">
            <div className="di-inline-tabs" role="tablist" aria-label="Playbook filter">
              {(["All", "Created by me", "Shared", "Firm-wide"] as PlaybookFilter[]).map((value) => (
                <button
                  aria-selected={filter === value}
                  key={value}
                  onClick={() => setFilter(value)}
                  role="tab"
                  type="button"
                >
                  {value}
                </button>
              ))}
            </div>
            <span className="di-spacer" />
            <label>
              <span className="di-visually-hidden">Creator</span>
              <select
                className="di-btn di-btn--secondary di-compact-select"
                onChange={(event) => setCreator(event.target.value)}
                value={creator}
              >
                {creators.map((option) => <option key={option}>{option}</option>)}
              </select>
            </label>
            <label className="di-search di-search--bordered">
              <SearchIcon />
              <span className="di-visually-hidden">Search playbooks</span>
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search playbooks"
                type="search"
                value={query}
              />
            </label>
          </div>

          <div className="di-playbooks__table-scroll">
            <table className="di-simple-table">
              <caption className="di-visually-hidden">Workspace playbooks</caption>
              <thead>
                <tr>
                  <th scope="col">Title</th>
                  <th scope="col">Last modified</th>
                  <th className="di-align-right" scope="col">Rules</th>
                  <th scope="col">Shared with</th>
                  <th className="di-align-right" scope="col">Runs</th>
                  <th scope="col">Created by</th>
                  <th scope="col"><span className="di-visually-hidden">Delete</span></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((playbook) => {
                  const selected = playbook.id === effectiveSelectedId;
                  return (
                    <tr key={playbook.id}>
                      <td>
                        <button
                          aria-pressed={selected}
                          className="di-playbook-title"
                          onClick={() => select(playbook)}
                          type="button"
                        >
                          {selected && <span aria-hidden="true" />}
                          {playbook.title}
                        </button>
                      </td>
                      <td className="di-muted">{playbook.modifiedAt}</td>
                      <td className="di-align-right">{playbook.ruleCount}</td>
                      <td><span className="di-tag">{playbook.sharedWith}</span></td>
                      <td className="di-align-right di-muted">{playbook.runCount ?? "None"}</td>
                      <td className="di-muted">{playbook.createdBy}</td>
                      <td className="di-align-right">
                        <button
                          aria-label={`Delete ${playbook.title}`}
                          className="di-icon-button"
                          disabled={!onDelete}
                          onClick={() => setDeleteTarget(playbook)}
                          title={!onDelete ? "Playbook deletion is not connected." : undefined}
                          type="button"
                        >
                          <TrashIcon />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="di-footnote">
            A playbook records the negotiating standard for a document type: the
            rule, its approved fallbacks and the escalation route. Selecting a
            row sets the playbook used by the review panel.
          </p>
        </div>
      </div>

      <aside className="di-playbooks__review" aria-label="Review a document">
        <div className="di-section-heading">
          <h2>Review a document</h2>
          <p>
            DI compares the document against every position in the selected
            playbook and returns tracked changes with the rationale for each edit.
          </p>
        </div>
        <fieldset className="di-playbook-radio-list">
          <legend>Playbook</legend>
          {playbooks.map((playbook) => (
            <label className="di-radio" key={playbook.id}>
              <input
                checked={playbook.id === effectiveSelectedId}
                name="review-playbook"
                onChange={() => select(playbook)}
                type="radio"
              />
              <span aria-hidden="true" />
              {playbook.title}
            </label>
          ))}
        </fieldset>
        <div className="di-drop-zone di-drop-zone--compact">
          <span className="di-accent-icon"><UploadIcon /></span>
          <strong>{reviewDocument ? reviewDocument.name : "Drop the counterparty draft"}</strong>
          <span>
            {reviewDocument?.pages != null
              ? `${reviewDocument.pages} pp. · ready to review`
              : ".docx preferred — tracked changes are written back into the original file"}
          </span>
        </div>
        <div className="di-button-row">
          <button
            className="di-btn di-btn--secondary"
            disabled={!onBrowseReviewDocument}
            onClick={onBrowseReviewDocument}
            title={!onBrowseReviewDocument ? "Draft upload is not connected." : undefined}
            type="button"
          >
            Browse
          </button>
          <button
            className="di-btn di-btn--secondary"
            disabled={!onChooseReviewDocumentFromRepository}
            onClick={onChooseReviewDocumentFromRepository}
            title={!onChooseReviewDocumentFromRepository
              ? "Repository selection is not connected."
              : undefined}
            type="button"
          >
            From Repository
          </button>
        </div>
        <button
          className="di-btn di-btn--primary di-btn--block"
          disabled={!onReview || !selectedPlaybook || !reviewDocument}
          onClick={() => {
            if (selectedPlaybook && reviewDocument) {
              void onReview?.(selectedPlaybook, reviewDocument);
            }
          }}
          title={!onReview
            ? "Playbook review is not connected."
            : !reviewDocument
              ? "Choose a counterparty draft first."
              : undefined}
          type="button"
        >
          Review against {selectedPlaybook?.title || "selected playbook"}
        </button>
      </aside>

      {deleteTarget && (
        <ConfirmDeletePlaybook
          busy={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async () => {
            if (!onDelete) return;
            setDeleting(true);
            try {
              await onDelete(deleteTarget);
              setDeleteTarget(null);
            } finally {
              setDeleting(false);
            }
          }}
          playbook={deleteTarget}
        />
      )}
    </section>
  );
}

function ConfirmDeletePlaybook({
  playbook,
  busy,
  onCancel,
  onConfirm,
}: {
  playbook: PlaybookListItem;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const headingId = "di-delete-playbook-title";
  return (
    <div className="di-dialog-backdrop">
      <section
        aria-labelledby={headingId}
        aria-modal="true"
        className="di-dialog di-dialog--small"
        role="dialog"
      >
        <header className="di-dialog__header">
          <h2 id={headingId}>Delete playbook?</h2>
          <button aria-label="Close" className="di-icon-button" onClick={onCancel} type="button">
            <CloseIcon />
          </button>
        </header>
        <div className="di-dialog__body">
          <p>
            <strong>{playbook.title}</strong> will be removed from the workspace.
            Reviews already run keep their tracked changes and rationales.
          </p>
        </div>
        <footer className="di-dialog__footer">
          <span className="di-spacer" />
          <button className="di-btn di-btn--secondary" disabled={busy} onClick={onCancel} type="button">
            Cancel
          </button>
          <button
            className="di-btn di-btn--primary"
            disabled={busy}
            onClick={() => void onConfirm()}
            type="button"
          >
            {busy ? "Deleting…" : "Delete playbook"}
          </button>
        </footer>
      </section>
    </div>
  );
}

export interface CreatePlaybookDialogProps {
  onClose: () => void;
  onStartWorkflow?: () => void;
}

export function CreatePlaybookDialog({
  onClose,
  onStartWorkflow,
}: CreatePlaybookDialogProps) {
  const headingId = "di-create-playbook-title";
  return (
    <div className="di-dialog-backdrop">
      <section
        aria-labelledby={headingId}
        aria-modal="true"
        className="di-dialog di-dialog--medium di-create-playbook-dialog"
        role="dialog"
      >
        <header className="di-dialog__header">
          <div>
            <h2 id={headingId}>Create a playbook</h2>
            <p>DI builds it with you from the materials you already have.</p>
          </div>
          <button aria-label="Close" className="di-icon-button" onClick={onClose} type="button">
            <CloseIcon />
          </button>
        </header>
        <div className="di-dialog__body">
          <button
            className="di-create-playbook-card"
            disabled={!onStartWorkflow}
            onClick={onStartWorkflow}
            title={!onStartWorkflow ? "The creation workflow is not connected." : undefined}
            type="button"
          >
            <span className="di-kicker"><PlusIcon /> Creation</span>
            <strong>Playbook creation workflow</strong>
            <span>
              DI guides you through building a playbook from what you already
              have — an existing playbook, a standard form, or a bank of
              executed precedents.
            </span>
            <ChevronIcon />
          </button>
        </div>
      </section>
    </div>
  );
}
